/**
 * Firestore MVP Search Provider
 *
 * Implements token-based search across all entities using searchTokens array-contains.
 * All entities use the same unified search pattern for consistency.
 *
 * Limitations (MVP):
 * - No fuzzy search
 * - No ranking engine
 * - Prefix-only matching (via pre-generated prefix tokens)
 *
 * Future: Can be swapped for Typesense without UI changes.
 *
 * Search Strategy (Unified searchTokens):
 * All entities use searchTokens with array-contains for the first token,
 * then client-side filtering for additional tokens (Firestore limitation).
 *
 * - Vehicle: searchTokens (brand, model, plate prefixes)
 * - Customer: searchTokens (name, email, phone prefixes)
 * - Sales Invoice: searchTokens (invoiceNo, customerName prefixes)
 * - Purchase Invoice: searchTokens (invoiceNo, supplierName prefixes)
 * - Work Order: searchTokens (title, id prefixes)
 * - Part/Inventory: searchTokens (partName, sku prefixes)
 *
 * Dialog Entity Names (must match useDialogUrlSync registrations):
 * - vehicle → VehiclesPage entity: 'vehicle'
 * - customer → CustomersPage entity: 'customer'
 * - salesInvoice → SalesInvoicesPage entity: 'salesInvoice'
 * - purchaseInvoice → PurchaseInvoicesPage entity: 'purchaseInvoice'
 * - workOrder → WorkOrdersTab entity: 'workOrder'
 * - inventoryItem → InventoryModule entity: 'inventoryItem' (NOT 'part')
 */

import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import type {
  SearchProvider,
  GlobalSearchResult,
  EntityType,
  SearchOptions,
} from './SearchProvider';

// ============================================================================
// Configuration
// ============================================================================

const RESULTS_PER_ENTITY = 10;
const MIN_QUERY_LENGTH = 2;
const MIN_TOKEN_LENGTH = 2; // Minimum token length for search (matches plate tokens)

// ============================================================================
// Collection References
// ============================================================================

function vehiclesCol(teamId: string) {
  return collection(db, 'teams', teamId, 'vehicles');
}

function customersCol(teamId: string) {
  return collection(db, 'teams', teamId, 'customers');
}

function salesInvoicesCol(teamId: string) {
  return collection(db, 'teams', teamId, 'salesInvoices');
}

function purchaseInvoicesCol(teamId: string) {
  return collection(db, 'teams', teamId, 'purchaseInvoices');
}

function workOrdersCol(teamId: string) {
  return collection(db, 'teams', teamId, 'workOrders');
}

function partStockLinesCol(teamId: string) {
  return collection(db, 'teams', teamId, 'partStockLines');
}

// ============================================================================
// Search Token Helpers
// ============================================================================

/**
 * Tokenize search input into words for multi-word search
 * Returns lowercase tokens, filtered by minimum length
 */
function tokenizeSearchInput(input: string, minLength: number = MIN_TOKEN_LENGTH): string[] {
  if (!input) return [];
  return input
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((token) => token.length >= minLength);
}

/**
 * Check if all search tokens match the document's searchTokens array
 * Used for client-side filtering after Firestore query (which only supports one array-contains)
 */
function matchesAllTokens(searchTokens: string[], docTokens: string[] | undefined): boolean {
  if (!docTokens || !Array.isArray(docTokens)) return false;
  if (searchTokens.length === 0) return true;
  // Every search token must be found in docTokens (prefix match)
  return searchTokens.every((searchToken) =>
    docTokens.some((docToken) => docToken.startsWith(searchToken))
  );
}

// ============================================================================
// Vehicle Search
// ============================================================================

interface VehicleDoc {
  id: string;
  licensePlate?: string;
  vin?: string;
  brand?: string;
  model?: string;
  year?: number;
  status?: string;
  deletedAt?: unknown;
  searchTokens?: string[];
}

/**
 * Search vehicles using searchTokens array-contains
 * Supports multi-word search (e.g., "bmw x5")
 */
async function searchVehicles(teamId: string, searchInput: string): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    // Query on first token only (Firestore limitation: one array-contains per query)
    const q = query(
      vehiclesCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as VehicleDoc;
        // Skip deleted
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapVehicleToResult(doc.id, data, teamId);
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Vehicle search failed:', error);
    return [];
  }
}

function mapVehicleToResult(id: string, data: VehicleDoc, teamId: string): GlobalSearchResult {
  const plate = data.licensePlate || '';
  const brand = data.brand || '';
  const model = data.model || '';
  const year = data.year;
  const vin = data.vin || '';

  // Title: plate or VIN
  const title = plate || vin || 'Onbekend voertuig';

  // Subtitle: brand model • year
  const subtitleParts = [brand, model].filter(Boolean).join(' ');
  const subtitle = year ? `${subtitleParts} • ${year}` : subtitleParts;

  return {
    entityType: 'vehicle',
    id,
    teamId,
    title,
    subtitle: subtitle || undefined,
    openTarget: {
      route: '/vehicles',
      dialog: 'vehicle',
      idParam: id,
    },
  };
}

// ============================================================================
// Customer Search
// ============================================================================

interface CustomerDoc {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  type?: 'private' | 'company';
  deletedAt?: unknown;
  searchTokens?: string[];
}

/**
 * Search customers using searchTokens array-contains
 * Supports multi-word search (e.g., "jan jansen")
 */
async function searchCustomers(teamId: string, searchInput: string): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    // Query on first token only (Firestore limitation: one array-contains per query)
    const q = query(
      customersCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as CustomerDoc;
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapCustomerToResult(doc.id, data, teamId);
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Customer search failed:', error);
    return [];
  }
}

function mapCustomerToResult(id: string, data: CustomerDoc, teamId: string): GlobalSearchResult {
  // Build display name
  const name =
    data.displayName ||
    data.companyName ||
    [data.firstName, data.lastName].filter(Boolean).join(' ') ||
    'Onbekende klant';

  // Subtitle: email/phone + type
  const contactInfo = data.email || data.phone || '';
  const typeLabel = data.type === 'company' ? 'Bedrijf' : 'Particulier';
  const subtitle = contactInfo ? `${contactInfo} • ${typeLabel}` : typeLabel;

  return {
    entityType: 'customer',
    id,
    teamId,
    title: name,
    subtitle,
    openTarget: {
      route: '/customers',
      dialog: 'customer',
      idParam: id,
    },
  };
}

// ============================================================================
// Invoice Search
// ============================================================================

interface InvoiceDoc {
  id: string;
  invoiceNo?: string;
  partyName?: string;
  customerName?: string;
  supplierName?: string;
  totalCents?: number;
  total?: number;
  vehiclePlates?: string[];
  primaryVehicleId?: string;
  vehicleId?: string;
  customerId?: string;
  partyId?: string;
  supplierId?: string;
  deletedAt?: unknown;
  searchTokens?: string[];
}

/**
 * Search sales invoices using searchTokens array-contains
 * Supports multi-word search (e.g., "SI-25 jan")
 */
async function searchSalesInvoices(
  teamId: string,
  searchInput: string
): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    const q = query(
      salesInvoicesCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as InvoiceDoc;
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapInvoiceToResult(doc.id, data, teamId, 'salesInvoice');
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Sales invoice search failed:', error);
    return [];
  }
}

/**
 * Search purchase invoices using searchTokens array-contains
 * Supports multi-word search (e.g., "PI-25 supplier")
 */
async function searchPurchaseInvoices(
  teamId: string,
  searchInput: string
): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    const q = query(
      purchaseInvoicesCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as InvoiceDoc;
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapInvoiceToResult(doc.id, data, teamId, 'purchaseInvoice');
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Purchase invoice search failed:', error);
    return [];
  }
}

function mapInvoiceToResult(
  id: string,
  data: InvoiceDoc,
  teamId: string,
  entityType: 'salesInvoice' | 'purchaseInvoice'
): GlobalSearchResult {
  const invoiceNo = data.invoiceNo || 'Onbekend';
  const isSales = entityType === 'salesInvoice';

  // Title: invoice number
  const title = invoiceNo;

  // Subtitle: type • plate • €total
  const typeLabel = isSales ? 'Verkoopfactuur' : 'Inkoopfactuur';
  const plate = data.vehiclePlates?.[0] || '';
  const totalCents = data.totalCents ?? data.total ?? 0;
  const totalEuros = typeof totalCents === 'number' ? (totalCents / 100).toFixed(2) : '0.00';

  const subtitleParts = [typeLabel];
  if (plate) subtitleParts.push(plate);
  subtitleParts.push(`€${totalEuros}`);
  const subtitle = subtitleParts.join(' • ');

  // Related IDs
  const vehicleId = data.primaryVehicleId || data.vehicleId;
  const customerId = data.customerId || data.partyId || data.supplierId;

  return {
    entityType,
    id,
    teamId,
    title,
    subtitle,
    relatedIds: {
      vehicleId: vehicleId || undefined,
      customerId: customerId || undefined,
    },
    openTarget: {
      route: isSales ? '/sales-invoices' : '/purchase-invoices',
      dialog: isSales ? 'salesInvoice' : 'purchaseInvoice',
      idParam: id,
    },
  };
}

// ============================================================================
// Work Order Search
// ============================================================================

interface WorkOrderDoc {
  id: string;
  title?: string;
  status?: string;
  vehicleId?: string;
  customerId?: string;
  assigneeId?: string;
  deletedAt?: unknown;
  searchTokens?: string[];
}

/**
 * Search work orders using searchTokens array-contains
 * Supports multi-word search (e.g., "WO-25 reparatie")
 */
async function searchWorkOrders(
  teamId: string,
  searchInput: string
): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    const q = query(
      workOrdersCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as WorkOrderDoc;
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapWorkOrderToResult(doc.id, data, teamId);
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Work order search failed:', error);
    return [];
  }
}

function mapWorkOrderToResult(id: string, data: WorkOrderDoc, teamId: string): GlobalSearchResult {
  const title = data.title || id || 'Onbekende werkorder';
  const status = data.status || '';

  // Subtitle: status
  const statusLabels: Record<string, string> = {
    NIEUW: 'Nieuw',
    PLANNED: 'Gepland',
    IN_PROGRESS: 'In uitvoering',
    DONE: 'Afgerond',
    INVOICED: 'Gefactureerd',
    CANCELED: 'Geannuleerd',
  };
  const subtitle = statusLabels[status] || status;

  return {
    entityType: 'workOrder',
    id,
    teamId,
    title,
    subtitle: subtitle || undefined,
    relatedIds: {
      vehicleId: data.vehicleId || undefined,
      customerId: data.customerId || undefined,
    },
    openTarget: {
      route: '/garage',
      dialog: 'workOrder',
      idParam: id,
    },
  };
}

// ============================================================================
// Part Stock Line Search
// ============================================================================

interface PartStockLineDoc {
  id: string;
  partName?: string;
  sku?: string;
  oemNumber?: string;
  quantityAvailable?: number;
  quantityPurchased?: number;
  supplierName?: string;
  deletedAt?: unknown;
  searchTokens?: string[];
}

/**
 * Search parts using searchTokens array-contains
 * Supports multi-word search (e.g., "remblok achter")
 */
async function searchParts(teamId: string, searchInput: string): Promise<GlobalSearchResult[]> {
  const tokens = tokenizeSearchInput(searchInput);
  if (tokens.length === 0) return [];

  const firstToken = tokens[0];

  try {
    const q = query(
      partStockLinesCol(teamId),
      where('searchTokens', 'array-contains', firstToken),
      limit(RESULTS_PER_ENTITY)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs
      .map((doc) => {
        const data = doc.data() as PartStockLineDoc;
        if (data.deletedAt) return null;
        // Client-side filter for additional tokens
        if (tokens.length > 1 && !matchesAllTokens(tokens, data.searchTokens)) {
          return null;
        }
        return mapPartToResult(doc.id, data, teamId);
      })
      .filter((r): r is GlobalSearchResult => r !== null);
  } catch (error) {
    logger.error('[FirestoreSearch] Part search failed:', error);
    return [];
  }
}

function mapPartToResult(id: string, data: PartStockLineDoc, teamId: string): GlobalSearchResult {
  const partName = data.partName || 'Onbekend onderdeel';
  const sku = data.sku || '';
  const quantity = data.quantityAvailable ?? data.quantityPurchased ?? 0;
  const supplier = data.supplierName || '';

  // Title: part name
  const title = partName;

  // Subtitle: SKU • quantity in stock • supplier
  const subtitleParts: string[] = [];
  if (sku) subtitleParts.push(sku);
  subtitleParts.push(`${quantity} op voorraad`);
  if (supplier) subtitleParts.push(supplier);
  const subtitle = subtitleParts.join(' • ');

  return {
    entityType: 'part',
    id,
    teamId,
    title,
    subtitle,
    openTarget: {
      route: '/inventory',
      dialog: 'inventoryItem', // Must match InventoryModule's useDialogUrlSync entity
      idParam: id,
    },
  };
}

// ============================================================================
// Main Provider Implementation
// ============================================================================

export class FirestoreSearchProvider implements SearchProvider {
  private activeRequestId = 0;

  async search(
    searchQuery: string,
    teamId: string,
    options?: SearchOptions
  ): Promise<GlobalSearchResult[]> {
    const trimmedQuery = searchQuery.trim();
    const scope = options?.scope;

    // Helper to check if we should search this entity type
    const shouldSearch = (entityType: EntityType): boolean => {
      return !scope || scope === entityType;
    };

    // Enforce minimum length
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      return [];
    }

    // Track request ID to ignore stale responses
    const requestId = ++this.activeRequestId;

    // Run searches in parallel - all entities now use unified searchTokens approach
    const searchPromises: Promise<GlobalSearchResult[]>[] = [];

    // Vehicle search
    if (shouldSearch('vehicle')) {
      searchPromises.push(searchVehicles(teamId, trimmedQuery));
    }

    // Customer search
    if (shouldSearch('customer')) {
      searchPromises.push(searchCustomers(teamId, trimmedQuery));
    }

    // Work order search
    if (shouldSearch('workOrder')) {
      searchPromises.push(searchWorkOrders(teamId, trimmedQuery));
    }

    // Part search
    if (shouldSearch('part')) {
      searchPromises.push(searchParts(teamId, trimmedQuery));
    }

    // Invoice searches
    if (shouldSearch('salesInvoice')) {
      searchPromises.push(searchSalesInvoices(teamId, trimmedQuery));
    }
    if (shouldSearch('purchaseInvoice')) {
      searchPromises.push(searchPurchaseInvoices(teamId, trimmedQuery));
    }

    try {
      const results = await Promise.all(searchPromises);

      // Check if this request is still active
      if (requestId !== this.activeRequestId) {
        logger.debug('[FirestoreSearch] Ignoring stale request', {
          requestId,
          activeRequestId: this.activeRequestId,
        });
        return [];
      }

      // Flatten and deduplicate
      const allResults = results.flat();
      const deduplicated = deduplicateResults(allResults);

      // Limit total results (max 60 with 6 entity types, 10 per entity)
      return deduplicated.slice(0, 60);
    } catch (error) {
      logger.error('[FirestoreSearch] Search failed:', error);
      throw error; // Re-throw so UI can show error state
    }
  }
}

/**
 * Deduplicate results by entityType:id
 */
function deduplicateResults(results: GlobalSearchResult[]): GlobalSearchResult[] {
  const seen = new Set<string>();
  return results.filter((result) => {
    const key = `${result.entityType}:${result.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

// Export singleton instance
export const firestoreSearchProvider = new FirestoreSearchProvider();
